import React from 'react';
import Icon from '../../../components/AppIcon';

const ServiceOverview = () => {
  const services = [
    {
      id: '01',
      icon: 'Sparkles',
      title: 'The Fresh Start',
      audience: 'For new businesses and solo entrepreneurs',
      description: 'Get your finances organized from the beginning with a bookkeeping foundation designed to grow with your business.',
      features: [
        'QuickBooks Online (QBO) setup guidance',
        'Chart of accounts review',
        'Bank and credit card transaction categorization',
        'Basic monthly financial organization',
        'Guidance on maintaining accurate records',
        'Weekly bookkeeping questions and guidance'
      ],
      price: 'Starting at $200/month',
      priceNote: 'For eligible businesses with simple bookkeeping needs',
      color: 'primary'
    },
    {
      id: '02',
      icon: 'Landmark',
      title: 'Financial Foundation',
      audience: 'For established solo entrepreneurs who need organized books',
      description: 'A dependable bookkeeping foundation for business owners who want their transactions organized and their financial records maintained consistently.',
      features: [
        'Transaction categorization and review',
        'Bank and credit card reconciliation',
        'QuickBooks Online organization',
        'Monthly financial record maintenance',
        'Periodic bookkeeping questions and guidance'
      ],
      price: '$300–$450/month',
      color: 'secondary'
    },
    {
      id: '03',
      icon: 'BookCheck',
      title: 'Complete Monthly Bookkeeping',
      audience: 'For businesses that want consistent, reliable bookkeeping',
      description: 'Keep your financial records current and organized throughout the year, with monthly reports that help you understand where your business stands.',
      features: [
        'Everything in Financial Foundation',
        'Monthly reconciliations for agreed accounts',
        'Profit and loss statement and balance sheet',
        'Financial record review and discrepancy follow-up',
        'Organized, tax-preparation-ready records',
        'Monthly email support for bookkeeping questions'
      ],
      price: '$450–$650/month',
      color: 'accent',
      recommended: true
    },
    {
      id: '04',
      icon: 'TrendingUp',
      title: 'Business Insight & Support',
      audience: 'For established businesses that want more than data entry',
      description: 'Go beyond keeping the books. Get help understanding financial reports, tracking outstanding payments, and identifying questions to address before they become bigger problems.',
      features: [
        'Everything in Complete Monthly Bookkeeping',
        'Invoice preparation and accounts receivable tracking',
        'Bill and accounts payable tracking',
        'Monthly financial report review',
        'Cash flow and expense trend observations',
        'Scheduled monthly financial review meeting'
      ],
      price: '$650–$950+/month',
      color: 'success'
    },
    {
      id: '05',
      icon: 'RefreshCcw',
      title: 'Books Reset & Recovery',
      audience: 'For businesses with overdue, inaccurate, or disorganized books',
      description: 'Get your bookkeeping back on track with a structured cleanup designed to restore organization, address discrepancies, and establish a sustainable monthly process.',
      features: [
        'Initial review of existing records',
        'Historical transaction categorization and corrections',
        'Catch-up bank and credit card reconciliations',
        'Identification of missing documentation and discrepancies',
        'QuickBooks Online cleanup and organization',
        'Written recommendations for ongoing bookkeeping'
      ],
      price: 'Starting at $1,200',
      priceNote: 'One-time project. Custom quote based on the number of months, accounts, and transactions involved.',
      color: 'warning'
    }
  ];

  const additionalServices = [
    {
      icon: 'Stethoscope',
      title: 'Bookkeeping Health Check',
      description: 'A focused review of bookkeeping organization, reconciliations, and potential issues.'
    },
    {
      icon: 'GraduationCap',
      title: 'QuickBooks Coaching',
      description: 'One-on-one instruction to help business owners understand and use QuickBooks Online.'
    },
    {
      icon: 'ChartNoAxesCombined',
      title: 'Financial Report Review',
      description: 'A guided review of financial statements, expense patterns, and questions to discuss with a tax professional or financial adviser as appropriate.'
    },
    {
      icon: 'FolderCheck',
      title: 'Virtual Administrative Support',
      description: 'Receipt organization, document management, and selected administrative tasks.'
    },
    {
      icon: 'WalletCards',
      title: 'Personal Finance Tracking',
      description: 'Customized expense tracking, budgeting, and financial organization using Excel or Google Sheets.'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: 'bg-primary/10',
        border: 'border-primary/20',
        icon: 'text-primary',
        hover: 'hover:bg-primary/5'
      },
      secondary: {
        bg: 'bg-secondary/10',
        border: 'border-secondary/20',
        icon: 'text-secondary',
        hover: 'hover:bg-secondary/5'
      },
      accent: {
        bg: 'bg-accent/10',
        border: 'border-accent/30',
        icon: 'text-accent',
        hover: 'hover:bg-accent/5'
      },
      success: {
        bg: 'bg-success/10',
        border: 'border-success/20',
        icon: 'text-success',
        hover: 'hover:bg-success/5'
      },
      warning: {
        bg: 'bg-warning/10',
        border: 'border-warning/20',
        icon: 'text-warning',
        hover: 'hover:bg-warning/5'
      }
    };

    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-background to-secondary/5 pt-24 pb-16 lg:pt-32 lg:pb-24">
      <div className="container-brand">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Online Bookkeeping Services
            <span className="text-brand-gradient block mt-2">Built for Every Stage</span>
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed max-w-3xl mx-auto">
            Choose the level of bookkeeping support that fits where your business is today and where you want it to go.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8 mb-16 items-stretch">
          {services.map((service, index) => {
            const colors = getColorClasses(service.color);

            return (
              <article
                key={service.id}
                className={`group relative flex flex-col bg-card border ${colors.border} rounded-xl p-6 lg:p-8 transition-all duration-normal ${colors.hover} hover:shadow-brand animate-fade-in ${service.recommended ? 'ring-2 ring-accent/20' : ''}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {service.recommended && (
                  <div className="self-start px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold tracking-wide uppercase mb-5">
                    Recommended Core Service
                  </div>
                )}

                <div className="flex items-center justify-between mb-5">
                  <div className={`inline-flex items-center justify-center w-12 h-12 ${colors.bg} rounded-lg`}>
                    <Icon name={service.icon} size={24} className={colors.icon} />
                  </div>
                  <span className="text-sm font-mono font-semibold text-text-secondary">{service.id}</span>
                </div>

                <div className="flex flex-col flex-1">
                  <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm font-semibold text-text-primary mb-4">{service.audience}</p>
                  <p className="text-text-secondary leading-relaxed mb-6">{service.description}</p>

                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start text-sm text-text-secondary leading-relaxed">
                        <Icon name="Check" size={16} className={`mr-2 mt-0.5 shrink-0 ${colors.icon}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={`mt-auto pt-5 border-t ${colors.border}`}>
                    <p className="text-xl font-bold text-foreground">{service.price}</p>
                    {service.priceNote && (
                      <p className="text-xs text-text-secondary leading-relaxed mt-2">{service.priceNote}</p>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mb-12">
          <div className="max-w-3xl mb-8">
            <p className="text-sm font-semibold uppercase tracking-widest text-secondary mb-3">Flexible support</p>
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-3">Additional Services</h3>
            <p className="text-text-secondary leading-relaxed">
              Focused support for business owners who need help with a specific part of their financial organization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service) => (
              <article key={service.title} className="floating-card p-6">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg mb-4">
                  <Icon name={service.icon} size={20} className="text-secondary" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">{service.title}</h4>
                <p className="text-sm text-text-secondary leading-relaxed">{service.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="bg-muted/70 border border-border rounded-xl p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <Icon name="Info" size={22} className="text-secondary shrink-0 mt-0.5" />
            <p className="text-sm text-text-secondary leading-relaxed">
              These are proposed positioning and pricing ranges, not verified market-rate quotes. Final pricing is based on transaction volume, number of accounts, complexity, reporting needs, and time required. Cleanup projects are quoted separately after an initial review.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceOverview;
